/**
 * Form Field Definitions for All 11 Airtable Form Types
 * Each form type has its own set of fields with specific validation and conditional logic
 *
 * Field Types:
 * - text: Single-line text input
 * - number: Numeric input
 * - select: Single-select dropdown
 * - multiselect: Multi-select dropdown
 * - radio: Yes/No radio buttons
 * - textarea: Multi-line text input
 * - richtext: Rich text editor (formatted text)
 * - file: File upload
 * - linkedrecord: Linked record from another table
 */

import { FORM_TYPES } from './positionFormMapping';

// Common fields that appear across multiple forms
const COMMON_FIELDS = {
  LAST_POSITION: {
    id: 'fldlastPosition',
    name: 'last_position',
    label: 'Your last position or title',
    type: 'text',
    required: true,
    placeholder: 'e.g., Senior Developer, Creative Director',
    helpText: ''
  },
  YEARS_EXPERIENCE_AGENCY: {
    id: 'fldyearsExperienceAgency',
    name: 'years_experience_agency',
    label: 'Years of experience working in a marketing, creative, or advertising agency',
    type: 'number',
    required: true,
    placeholder: '',
    helpText: ''
  },
  CLIENT_SECTORS: {
    id: 'fldclientSectors',
    name: 'client_sectors',
    label: 'What types of clients or sectors have you worked with before?',
    type: 'text',
    required: true,
    placeholder: 'e.g., Retail, Healthcare, Technology, Automotive',
    helpText: 'List the industries or client types you have experience with'
  }
};

// TECH Form Fields (pagysJOea57Lcl1Fb)
const TECH_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldtechStack',
    name: 'tech_stack',
    label: 'What is your primary tech stack?',
    type: 'textarea',
    required: true,
    placeholder: 'e.g., React, Node.js, Python, AWS, Docker',
    helpText: 'e.g., React, Node.js, Python, AWS, Docker'
  },
  {
    id: 'fldgithubPortfolio',
    name: 'github_portfolio',
    label: 'GitHub profile or portfolio link',
    type: 'text',
    required: false,
    placeholder: 'https://github.com/yourusername',
    helpText: 'Share your code samples or portfolio'
  },
  {
    id: 'fldagileExperience',
    name: 'agile_experience',
    label: 'Do you have experience working in Agile/Scrum environments?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldremoteWork',
    name: 'remote_work_preference',
    label: 'Are you open to remote work?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// TECH LEADERSHIP Form Fields (pagsL2BDIpGCBAU36)
const TECH_LEADERSHIP_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldteamSize',
    name: 'team_size_managed',
    label: 'What is the largest team you have managed?',
    type: 'number',
    required: true,
    placeholder: 'Number of people',
    helpText: ''
  },
  {
    id: 'fldleadershipStyle',
    name: 'leadership_style',
    label: 'Describe your leadership style',
    type: 'textarea',
    required: true,
    placeholder: 'Your approach to team management and mentorship',
    helpText: ''
  },
  {
    id: 'fldtechnicalStrategy',
    name: 'technical_strategy',
    label: 'Have you been responsible for technical strategy and architecture decisions?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldbudgetManagement',
    name: 'budget_management',
    label: 'Do you have experience managing technology budgets?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldvendorManagement',
    name: 'vendor_management',
    label: 'Have you managed external vendors or development partners?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// CREATIVE Form Fields (pagMyCFGOv0nmA2w4)
const CREATIVE_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldportfolioLink',
    name: 'portfolio_link',
    label: 'Portfolio link (Behance, personal website, etc.)',
    type: 'text',
    required: true,
    placeholder: 'https://behance.net/yourportfolio',
    helpText: 'Share your best work'
  },
  {
    id: 'fldcreativeTools',
    name: 'creative_tools',
    label: 'What creative tools and software do you use?',
    type: 'multiselect',
    required: true,
    options: [
      'Adobe Photoshop',
      'Adobe Illustrator',
      'Adobe InDesign',
      'Adobe XD',
      'Figma',
      'Sketch',
      'After Effects',
      'Premiere Pro',
      'Final Cut Pro',
      'Cinema 4D',
      'Blender',
      'Other'
    ]
  },
  {
    id: 'fldcreativeSpecialty',
    name: 'creative_specialty',
    label: 'What is your creative specialty?',
    type: 'select',
    required: true,
    options: [
      'Brand Identity',
      'Digital Design',
      'Print Design',
      'Copywriting',
      'Art Direction',
      'Illustration',
      'Motion Graphics',
      'UI/UX Design',
      'Strategy',
      'Content Creation',
      'Other'
    ]
  },
  {
    id: 'fldawardWinning',
    name: 'award_winning_work',
    label: 'Have you worked on award-winning campaigns?',
    type: 'radio',
    required: false,
    options: ['Yes', 'No']
  },
  {
    id: 'fldcampaignExamples',
    name: 'campaign_examples',
    label: 'Describe a campaign you\'re most proud of',
    type: 'richtext',
    required: true,
    placeholder: 'Share the brief, your role, and the impact',
    helpText: ''
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// VIDEO/PHOTO Form Fields (pagFoHqpQoGQa0eql)
const VIDEO_PHOTO_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldportfolioReel',
    name: 'portfolio_reel',
    label: 'Portfolio link or showreel',
    type: 'text',
    required: true,
    placeholder: 'https://vimeo.com/yourname or https://youtube.com/@yourname',
    helpText: 'Share your best visual work'
  },
  {
    id: 'fldcameraEquipment',
    name: 'camera_equipment',
    label: 'What camera equipment do you own or are proficient with?',
    type: 'textarea',
    required: true,
    placeholder: 'e.g., Sony A7S III, Canon C300, RED',
    helpText: ''
  },
  {
    id: 'fldproductionTypes',
    name: 'production_types',
    label: 'What types of productions have you worked on?',
    type: 'multiselect',
    required: true,
    options: [
      'Commercial/TVC',
      'Corporate Video',
      'Documentary',
      'Music Video',
      'Event Coverage',
      'Social Media Content',
      'Product Photography',
      'Fashion Photography',
      'Food Photography',
      'Lifestyle Photography',
      'Other'
    ]
  },
  {
    id: 'fldeditingSoftware',
    name: 'editing_software',
    label: 'What editing/post-production software do you use?',
    type: 'multiselect',
    required: true,
    options: [
      'Adobe Premiere Pro',
      'Final Cut Pro',
      'DaVinci Resolve',
      'After Effects',
      'Adobe Lightroom',
      'Capture One',
      'Other'
    ]
  },
  {
    id: 'fldonSetExperience',
    name: 'on_set_experience',
    label: 'Do you have experience managing on-set operations?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// POST PRODUCTION Form Fields (pagg2fy8v53X4BeNe)
const POST_PRODUCTION_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldpostProductionReel',
    name: 'post_production_reel',
    label: 'Showreel or portfolio link',
    type: 'text',
    required: true,
    placeholder: 'https://vimeo.com/yourname or https://behance.net/yourname',
    helpText: 'Share examples of your post-production work'
  },
  {
    id: 'fldpostSoftware',
    name: 'post_software',
    label: 'What post-production software do you specialize in?',
    type: 'multiselect',
    required: true,
    options: [
      'Adobe After Effects',
      'Adobe Premiere Pro',
      'Final Cut Pro',
      'DaVinci Resolve',
      'Cinema 4D',
      'Blender',
      'Nuke',
      'Flame',
      'Avid Media Composer',
      'Pro Tools',
      'Logic Pro',
      'Other'
    ]
  },
  {
    id: 'fldpostSpecialty',
    name: 'post_specialty',
    label: 'What is your post-production specialty?',
    type: 'select',
    required: true,
    options: [
      'Video Editing',
      'Motion Graphics',
      '2D Animation',
      '3D Animation',
      'VFX/Compositing',
      'Color Grading',
      'Sound Design',
      'Audio Engineering',
      'Other'
    ]
  },
  {
    id: 'fldtightDeadlines',
    name: 'tight_deadlines_experience',
    label: 'Are you comfortable working with tight deadlines and quick turnarounds?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldcomplexProjects',
    name: 'complex_projects',
    label: 'Describe the most complex post-production project you\'ve worked on',
    type: 'richtext',
    required: true,
    placeholder: 'Describe the project, your role, and outcome',
    helpText: ''
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// SOCIAL MEDIA Form Fields (pagv51R17SEmO3KDO)
const SOCIAL_MEDIA_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldluxuryExperience',
    name: 'luxury_industry_experience',
    label: 'Do you have experience working with luxury brands?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldsocialPlatforms',
    name: 'social_platforms_expertise',
    label: 'Which social media platforms do you have the most experience with?',
    type: 'multiselect',
    required: true,
    options: [
      'Instagram',
      'Facebook',
      'Twitter/X',
      'LinkedIn',
      'TikTok',
      'YouTube',
      'Snapchat',
      'Pinterest',
      'Other'
    ]
  },
  {
    id: 'fldpaidSocialAds',
    name: 'paid_social_ads_experience',
    label: 'Do you have experience managing paid social media advertising (Google Ads, Facebook Ads, etc.)?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldteamManagement',
    name: 'team_management_experience',
    label: 'Have you managed a team before?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldengagementStrategy',
    name: 'engagement_strategy',
    label: 'Describe your approach to building engagement on social media',
    type: 'richtext',
    required: true,
    placeholder: 'Your strategy for community building and growth',
    helpText: ''
  },
  {
    id: 'fldbrandAdmiration',
    name: 'brand_admiration',
    label: 'Which brand\'s social media do you admire most and why?',
    type: 'textarea',
    required: true,
    placeholder: 'Brand that inspires your social media work',
    helpText: ''
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// ACCOUNT MANAGEMENT Form Fields (pagJRtMOJZ5hWuMSq)
const ACCOUNT_MANAGEMENT_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldluxuryGovtExperience',
    name: 'luxury_govt_experience',
    label: 'Do you have experience working with luxury or government sector clients?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldsalesFocus',
    name: 'sales_focus',
    label: 'Are you more focused on sales or account management?',
    type: 'select',
    required: true,
    options: [
      'Primarily Sales (New Business)',
      'Primarily Account Management (Existing Clients)',
      'Equal Balance of Both',
      'Other'
    ]
  },
  {
    id: 'fldpipelineManagement',
    name: 'pipeline_management',
    label: 'Do you have experience managing a sales pipeline and forecasting?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldcommissionStructure',
    name: 'commission_structure_preference',
    label: 'What is your preferred commission structure?',
    type: 'select',
    required: false,
    options: [
      'Base salary only',
      'Base + Commission',
      'High commission, lower base',
      'Flexible/Open to discussion'
    ]
  },
  {
    id: 'fldclientRelationship',
    name: 'client_relationship_example',
    label: 'Share an example of how you built a strong client relationship',
    type: 'richtext',
    required: true,
    placeholder: 'Describe the situation, your approach, and the outcome',
    helpText: ''
  },
  {
    id: 'fldlinkedinPost',
    name: 'linkedin_post_draft',
    label: 'If you were to post about joining ASTUDIO on LinkedIn, what would you write? (Draft a short post)',
    type: 'textarea',
    required: false,
    placeholder: 'Draft your LinkedIn announcement',
    helpText: 'This helps us understand your communication style'
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// PRODUCTION Form Fields (paglZEkkgTA3thvTB)
const PRODUCTION_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldproductionManagement',
    name: 'production_management_areas',
    label: 'Which areas of production management are you most experienced in?',
    type: 'multiselect',
    required: true,
    options: [
      'Budget Management',
      'Schedule Coordination',
      'Talent Management',
      'Location Scouting',
      'Vendor Relations',
      'On-Set Logistics',
      'Post-Production Coordination',
      'Client Relations',
      'Other'
    ]
  },
  {
    id: 'fldvideoProductionExp',
    name: 'video_production_experience',
    label: 'How many years of experience do you have specifically in video production?',
    type: 'number',
    required: true,
    placeholder: 'Years',
    helpText: ''
  },
  {
    id: 'fldmultipleProjects',
    name: 'multiple_projects_management',
    label: 'Are you comfortable managing multiple productions simultaneously?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldcrisisManagement',
    name: 'crisis_management_example',
    label: 'Share an example of how you handled a production crisis or unexpected challenge',
    type: 'richtext',
    required: true,
    placeholder: 'Describe the situation, actions, and resolution',
    helpText: ''
  },
  {
    id: 'fldproductionNetwork',
    name: 'production_network',
    label: 'Do you have an established network of production vendors and freelancers?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// HR & OPERATIONS Form Fields (pagR9N12Zy8XIcaBv)
const HR_OPERATIONS_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'fldhrSpecialty',
    name: 'hr_specialty',
    label: 'What is your primary area of HR expertise?',
    type: 'select',
    required: true,
    options: [
      'Talent Acquisition/Recruitment',
      'Employee Relations',
      'Compensation & Benefits',
      'Learning & Development',
      'HR Operations',
      'Performance Management',
      'Organizational Development',
      'Generalist (All Areas)',
      'Other'
    ]
  },
  {
    id: 'fldhrisExperience',
    name: 'hris_experience',
    label: 'What HRIS or HR software have you used?',
    type: 'textarea',
    required: false,
    placeholder: 'e.g., Workday, BambooHR, SAP SuccessFactors, ADP',
    helpText: ''
  },
  {
    id: 'fldemployeeCount',
    name: 'employee_count_supported',
    label: 'What is the largest employee headcount you have supported?',
    type: 'number',
    required: true,
    placeholder: 'Number of employees',
    helpText: ''
  },
  {
    id: 'fldlaborLaw',
    name: 'uae_labor_law_knowledge',
    label: 'Do you have knowledge of UAE labor law?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldchangeManagement',
    name: 'change_management_experience',
    label: 'Have you been involved in organizational change management or restructuring?',
    type: 'radio',
    required: false,
    options: ['Yes', 'No']
  },
  {
    id: 'fldhrChallenge',
    name: 'hr_challenge_example',
    label: 'Describe an HR or operations challenge you successfully resolved',
    type: 'richtext',
    required: true,
    placeholder: 'Share the challenge, your approach, and the outcome',
    helpText: ''
  }
];

// DIGITAL Form Fields (pagQ9NjptujXtXTtR)
const DIGITAL_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  COMMON_FIELDS.YEARS_EXPERIENCE_AGENCY,
  {
    id: 'flddigitalChannels',
    name: 'digital_channels_expertise',
    label: 'Which digital marketing channels do you specialize in?',
    type: 'multiselect',
    required: true,
    options: [
      'Google Ads (Search)',
      'Google Display Network',
      'YouTube Advertising',
      'Facebook/Instagram Ads',
      'LinkedIn Ads',
      'Twitter/X Ads',
      'TikTok Ads',
      'Programmatic Display',
      'SEO',
      'SEM',
      'Email Marketing',
      'Affiliate Marketing',
      'Other'
    ]
  },
  {
    id: 'fldanalyticsTools',
    name: 'analytics_tools',
    label: 'What analytics and tracking tools do you use?',
    type: 'multiselect',
    required: true,
    options: [
      'Google Analytics (GA4)',
      'Google Tag Manager',
      'Google Data Studio/Looker',
      'Adobe Analytics',
      'Facebook Business Manager',
      'Hotjar',
      'SEMrush',
      'Ahrefs',
      'Other'
    ]
  },
  {
    id: 'fldbudgetManaged',
    name: 'largest_budget_managed',
    label: 'What is the largest monthly digital marketing budget you have managed?',
    type: 'select',
    required: true,
    options: [
      'Less than $10,000',
      '$10,000 - $50,000',
      '$50,000 - $100,000',
      '$100,000 - $250,000',
      '$250,000 - $500,000',
      'Over $500,000'
    ]
  },
  {
    id: 'fldperformanceMetrics',
    name: 'key_performance_metrics',
    label: 'What performance metrics do you focus on most?',
    type: 'textarea',
    required: true,
    placeholder: 'e.g., ROAS, CPA, CTR, Conversion Rate, CAC',
    helpText: ''
  },
  {
    id: 'fldcampaignSuccess',
    name: 'campaign_success_story',
    label: 'Share a digital campaign success story',
    type: 'richtext',
    required: true,
    placeholder: 'Campaign objectives, strategy, and results achieved',
    helpText: 'Include specific metrics and ROI if possible'
  },
  COMMON_FIELDS.CLIENT_SECTORS
];

// FINANCE Form Fields (pagCCLZi0TfdX1bEi)
const FINANCE_FIELDS = [
  COMMON_FIELDS.LAST_POSITION,
  {
    id: 'fldfinanceYears',
    name: 'years_in_finance',
    label: 'Years of experience in finance',
    type: 'select',
    required: true,
    options: [
      'Less than 1 year',
      '1-2 years',
      '3-5 years',
      '6-10 years',
      'More than 10 years'
    ]
  },
  {
    id: 'fldaccountingSoftware',
    name: 'accounting_software',
    label: 'What accounting software are you proficient with?',
    type: 'multiselect',
    required: true,
    options: [
      'QuickBooks',
      'Xero',
      'SAP',
      'Oracle',
      'Sage',
      'Zoho Books',
      'NetSuite',
      'Microsoft Dynamics',
      'Other'
    ]
  },
  {
    id: 'fldbookkeepingExp',
    name: 'bookkeeping_experience',
    label: 'Do you have hands-on bookkeeping experience?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldfinancialReporting',
    name: 'financial_reporting_experience',
    label: 'Have you prepared financial reports and statements?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldauditExperience',
    name: 'audit_experience',
    label: 'Do you have audit experience (internal or external)?',
    type: 'radio',
    required: false,
    options: ['Yes', 'No']
  },
  {
    id: 'fldtaxCompliance',
    name: 'uae_tax_compliance',
    label: 'Are you familiar with UAE tax compliance and VAT regulations?',
    type: 'select',
    required: true,
    options: ['Yes', 'No']
  },
  {
    id: 'fldfinancePriorities',
    name: 'finance_team_priorities',
    label: 'What do you think are the most important priorities for a finance team in an agency?',
    type: 'textarea',
    required: true,
    placeholder: 'Share your perspective on key finance priorities',
    helpText: ''
  }
];

// Export all form definitions mapped to form types
export const FORM_DEFINITIONS = {
  [FORM_TYPES.TECH]: TECH_FIELDS,
  [FORM_TYPES.TECH_LEADERSHIP]: TECH_LEADERSHIP_FIELDS,
  [FORM_TYPES.CREATIVE]: CREATIVE_FIELDS,
  [FORM_TYPES.VIDEO_PHOTO]: VIDEO_PHOTO_FIELDS,
  [FORM_TYPES.POST_PRODUCTION]: POST_PRODUCTION_FIELDS,
  [FORM_TYPES.SOCIAL_MEDIA]: SOCIAL_MEDIA_FIELDS,
  [FORM_TYPES.ACCOUNT_MANAGEMENT]: ACCOUNT_MANAGEMENT_FIELDS,
  [FORM_TYPES.PRODUCTION]: PRODUCTION_FIELDS,
  [FORM_TYPES.HR_OPERATIONS]: HR_OPERATIONS_FIELDS,
  [FORM_TYPES.DIGITAL]: DIGITAL_FIELDS,
  [FORM_TYPES.FINANCE]: FINANCE_FIELDS
};

/**
 * Get form fields for a specific form type
 * @param {string} formType - Form type constant from FORM_TYPES
 * @returns {Array} Array of field definitions
 */
export function getFormFields(formType) {
  return FORM_DEFINITIONS[formType] || [];
}

/**
 * Get a specific field definition by name from a form type
 * @param {string} formType - Form type constant
 * @param {string} fieldName - Field name to search for
 * @returns {Object|null} Field definition or null if not found
 */
export function getFieldByName(formType, fieldName) {
  const fields = getFormFields(formType);
  return fields.find(field => field.name === fieldName) || null;
}

/**
 * Get all required fields for a form type
 * @param {string} formType - Form type constant
 * @returns {Array} Array of required field definitions
 */
export function getRequiredFields(formType) {
  const fields = getFormFields(formType);
  return fields.filter(field => field.required === true);
}

/**
 * Validate form data against form type requirements
 * @param {string} formType - Form type constant
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with { isValid: boolean, errors: Array }
 */
export function validateFormData(formType, formData) {
  const requiredFields = getRequiredFields(formType);
  const errors = [];

  requiredFields.forEach(field => {
    const value = formData[field.name];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors.push({
        field: field.name,
        label: field.label,
        message: `${field.label} is required`
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

export default FORM_DEFINITIONS;
